import React, { CSSProperties } from 'react';

import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';

import {
  TABLE_CELL_HEIGHT_PX,
  Table,
  createColumnWidths,
  createColumnsWidths,
  createPredefinedColumnWidths,
} from '@qality/common-components/src/atoms/Table';
import { Folder } from '@qality/common-model';

import { useTestCaseRepositoryFiltersContext } from '../TestCaseRepositoryFilters';
import { useTestCaseRepositoryFoldersContext } from '../TestCaseRepositoryFoldersProvider';
import { useTestCaseRepositoryIssuesContext } from '../TestCaseRepositoryIssuesProvider';
import { useTestCaseRepositorySelectionContext } from '../TestCaseRepositorySelectionProvider';
import { TestCaseRepositoryCoverActions } from './TestCaseRepositoryCoverActions';
import { ContainerStyled } from './TestCaseRepositoryTable.styled';
import { TestCaseRepositoryTableBulkSelectCheckbox } from './TestCaseRepositoryTableBulkSelectCheckbox';
import { TableBodyRowEmpty } from './rows/TableBodyRowEmpty';
import { TableBodyRowsSkeleton } from './rows/TableBodyRowEmpty/TableBodyRowsSkeleton';
import { TestCaseRepositoryNoIssueTableRow } from './rows/TestCaseRepositoryNoIssueTableRow';
import { TestCaseRepositoryTableRow } from './rows/TestCaseRepositoryTableRow';

export type Columns =
  | 'dnd'
  | 'checkbox'
  | 'key'
  | 'summary'
  | 'project'
  | 'priority'
  | 'status'
  | 'assignee'
  | 'menu';

export const ColumnsWidths = createColumnsWidths<Columns>({
  dnd: createPredefinedColumnWidths('dnd'),
  checkbox: createPredefinedColumnWidths('checkbox'),
  key: createColumnWidths('15%', '15%', '0'),
  summary: createColumnWidths('27%', '27%', '0'),
  project: createColumnWidths('23%', '23%', '0'),
  priority: createColumnWidths('9%', '9%', '0'),
  status: createColumnWidths('11%', '11%', '0'),
  assignee: createColumnWidths('9%', '9%', '0'),
  menu: createColumnWidths('6%', '6%', '0'),
});

export const NUMBER_OF_COLUMNS = Object.keys(ColumnsWidths).length;

type Props = {
  numberOfSkeletonRows?: number;
  folder?: Folder;
  minRows?: number;
};

export default function TestCaseRepositoryTable({
  numberOfSkeletonRows = 10,
  folder = null,
  minRows = 10,
}: Props): JSX.Element {
  const {
    issues,
    loading,
    hasMoreIssues,
    empty,
    searchIssues,
    searchMoreIssues,
    selectionKey,
  } = useTestCaseRepositoryIssuesContext();

  const { isIssueSelected, toggleSelectIssue } =
    useTestCaseRepositorySelectionContext();

  const { currentView } = useTestCaseRepositoryFoldersContext();

  const { isFilterApplied, undoFilters } =
    useTestCaseRepositoryFiltersContext();

  const getTableHead = () => {
    return (
      <Table.Head>
        <Table.Tr>
          <Table.DndTh widths={ColumnsWidths.dnd} aria-label='Drag and drop' />

          <Table.CheckboxTh widths={ColumnsWidths.checkbox}>
            <Table.ContentHelper
              height='compact'
              alignItems='center'
              justifyContent='center'
            >
              <TestCaseRepositoryTableBulkSelectCheckbox
                isDisabled={loading !== 'none'}
                folder={folder}
              />
            </Table.ContentHelper>
          </Table.CheckboxTh>

          <Table.Th widths={ColumnsWidths.key}>Key</Table.Th>

          <Table.Th widths={ColumnsWidths.summary}>Summary</Table.Th>

          <Table.Th widths={ColumnsWidths.project}>Project</Table.Th>

          <Table.Th widths={ColumnsWidths.priority}>
            <Table.ContentHelper height='compact' justifyContent='center'>
              Priority
            </Table.ContentHelper>
          </Table.Th>

          <Table.Th widths={ColumnsWidths.status}>Status</Table.Th>

          <Table.Th widths={ColumnsWidths.assignee}>
            <Table.ContentHelper height='compact' justifyContent='center'>
              Assignee
            </Table.ContentHelper>
          </Table.Th>

          <Table.Th widths={ColumnsWidths.menu}>{/* Empty */}</Table.Th>

          <Table.FillerTh
            widths={createPredefinedColumnWidths('scrollbar-gutter')}
          >
            {/* Extra padding to align header and body columns */}
          </Table.FillerTh>
        </Table.Tr>
      </Table.Head>
    );
  };

  const getTableBody = () => {
    return <Table.Body>{getTableBodyRows()}</Table.Body>;
  };

  const getTableBodyRows = () => {
    if (empty !== 'none') {
      return <TableBodyRowEmpty />;
    }

    const isItemLoaded = (index: number) => {
      return issues[index] !== undefined;
    };

    const loadMoreItems =
      loading === 'none' ? searchMoreIssues : () => undefined;

    const { showSkeletonRows, itemCount } = (() => {
      if (loading === 'init' || loading === 'search') {
        return { showSkeletonRows: true, itemCount: numberOfSkeletonRows };
      }

      return {
        showSkeletonRows: false,
        itemCount: hasMoreIssues ? issues.length + 1 : issues.length,
      };
    })();

    function VirtualListItem({
      index,
      style,
    }: {
      index: number;
      style: CSSProperties;
    }): JSX.Element {
      if (showSkeletonRows || !isItemLoaded(index)) {
        return <TableBodyRowsSkeleton numberOfRows={1} style={style} />;
      }

      const issue = issues[index];
      if (issue.key) {
        return (
          <TestCaseRepositoryTableRow
            style={style}
            issue={issue}
            isSelected={isIssueSelected(selectionKey, issue)}
            toggleSelectIssue={() =>
              toggleSelectIssue(selectionKey, issue, folder)
            }
            folder={folder}
          />
        );
      }

      return <TestCaseRepositoryNoIssueTableRow style={style} />;
    }

    return (
      <AutoSizer>
        {({ height, width }) => (
          <InfiniteLoader
            isItemLoaded={isItemLoaded}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
          >
            {({ onItemsRendered, ref }) => (
              <FixedSizeList
                height={height}
                width={width}
                itemSize={TABLE_CELL_HEIGHT_PX}
                itemCount={itemCount}
                onItemsRendered={onItemsRendered}
                ref={ref}
                style={{ scrollbarGutter: 'stable' }}
              >
                {VirtualListItem}
              </FixedSizeList>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
    );
  };

  return (
    <ContainerStyled minRows={Math.min(issues.length, minRows)}>
      <Table.Cover
        isVisible={!isFilterApplied && currentView === 'all-test-cases'}
      >
        <TestCaseRepositoryCoverActions
          onFilterButtonClick={searchIssues}
          onUndoButtonClick={undoFilters}
        />
      </Table.Cover>
      <Table.Container isScrollable={false}>
        <Table useFixedLayout fullHeight={empty === 'none'}>
          {getTableHead()}
          {getTableBody()}
        </Table>
      </Table.Container>
    </ContainerStyled>
  );
}
